/* Lab 1.2 · PID 与控制频率: an inverted pendulum on a motor, held up by a PID controller.

   The physics: a rod pivoting at its base, θ measured from upright. Gravity tips it over,
   θ'' = (g/l)·sin θ − c·θ' + u, and the motor's command u (an angular acceleration, capped) pushes back.
   The controller only acts at its own rate: every 1/f seconds it reads the angle (with a little
   sensor noise), computes u = P·e + I·∫e + D·ė with e = θ* − θ, and holds u until the next read.
   Simulated at 1 kHz. The scripted take was tuned against the same model in Python:
     0–8 s   P 60, D 6: it stands, but leans past the target (the offset I will remove later)
     8–16 s  P up to 110 with D off: it swings back and forth; D back on at 11.8 s: it settles
     16–25 s I on at 16.8 s: the offset closes; the rate drops from 19.8 s: it wobbles, then falls */
import * as THREE from "three";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";
import { C, q, recorded, createStage, floor, run, toScreen, rng, clamp, span, el } from "../kit/stage";
import { Chart } from "../kit/chart";
import { localizePage, tr, type Dict } from "../kit/i18n";
import STRINGS from "./i18n.json";

localizePage(STRINGS as Dict);

const G_L = 9.81 / .5, FRICTION = .05, U_MAX = 90, NOISE = .25 * Math.PI / 180;
const DT = 1 / 1000, FALL = 84 * Math.PI / 180, ROD = .5;
const rad = (d: number): number => d * Math.PI / 180, deg = (r: number): number => r * 180 / Math.PI;

interface Gains { P: number; I: number; D: number; f: number; target: number }   // target in degrees

// --- the scripted take, gains as a function of time
const TG = 12;
function scripted(t: number): Gains & { hot: string } {
  if (t < 8.4) return { P: 60, I: 0, D: 6, f: 200, target: TG, hot: "" };
  if (t < 11.8) return { P: 60 + 50 * Math.min(1, (t - 8.4) / .25), I: 0, D: 0, f: 200, target: TG, hot: t < 9.6 ? "P" : "" };
  if (t < 16.8) return { P: 110, I: 0, D: Math.min(8, (t - 11.8) / .4 * 8), f: 200, target: TG, hot: t < 13 ? "D" : "" };
  const I = Math.min(60, (t - 16.8) / .5 * 60);
  const f = t < 19.8 ? 200 : Math.max(6, 200 * Math.exp(-(t - 19.8) * 1.8));
  return { P: 110, I, D: 8, f, target: TG, hot: t < 18 ? "I" : t >= 19.6 && t < 22.5 ? "F" : "" };
}

// --- state
interface State {
  t: number; th: number; om: number; integ: number; u: number; prev: number | null; last: number;
  fell: boolean; samples: number; flash: number; history: [number, number][]; histClock: number; noise: () => number;
}
const fresh = (): State => ({ t: 0, th: rad(2), om: 0, integ: 0, u: 0, prev: null, last: -1, fell: false, samples: 0, flash: 0,
  history: [], histClock: 0, noise: rng(12) });
let S = fresh();
const user: Gains = { P: 60, I: 20, D: 6, f: 200, target: 0 };
let kick = 0;
const gains = (): Gains => recorded ? scripted(S.t) : user;

function step(): void {
  const g = gains();
  if (S.t - S.last >= 1 / g.f - 1e-9) {           // 看 · 想: a sample and a new command, at the controller's rate
    const meas = S.th + (S.noise() * 2 - 1) * NOISE;
    const e = rad(g.target) - meas;
    const de = S.prev === null ? 0 : (e - S.prev) * g.f;
    S.integ = g.I > 0 ? clamp(S.integ + e / g.f, -1.5, 1.5) : 0;   // anti-windup: nothing stored while I is off
    S.u = clamp(g.P * e + g.I * S.integ + g.D * de, -U_MAX, U_MAX);
    S.prev = e;
    S.last = S.t;
    S.samples++;
    S.flash = 1;
  }
  if (kick) { S.om += kick; kick = 0; }
  if (!S.fell) {                                   // 动: gravity against the motor
    S.om += (G_L * Math.sin(S.th) - FRICTION * S.om + S.u) * DT;
    S.th += S.om * DT;
    if (Math.abs(S.th) >= FALL) { S.th = Math.sign(S.th) * FALL; S.om = 0; S.fell = true; }
  }
  S.flash = Math.max(0, S.flash - DT * 12);
  S.t += DT;
  S.histClock += DT;
  if (S.histClock >= 1 / 60) {
    S.histClock = 0;
    S.history.push([S.t, deg(S.th)]);
    if (S.history.length > 8 * 60 + 2) S.history.shift();
  }
}

// --- the scene: a motor housing, an axle, the rod and its bob; a ghost rod at the target
const canvas = el<HTMLCanvasElement>("stage");
const { scene, camera, render } = createStage(canvas);
floor(scene);
const metal = (color: number, metalness = .75, roughness = .4) => new THREE.MeshStandardMaterial({ color, metalness, roughness });
const glow = (color: number, k = 2.2) => new THREE.MeshStandardMaterial({ color, emissive: color, emissiveIntensity: k, roughness: .4 });
const PIVOT = new THREE.Vector3(0, .34, 0);
{
  const plinth = new THREE.Mesh(new THREE.BoxGeometry(.34, .04, .22), metal(0x121722, .5, .6));
  plinth.position.y = .02;
  const post = new THREE.Mesh(new THREE.BoxGeometry(.1, PIVOT.y - .04, .1), metal(0x2c3444));
  post.position.y = .04 + (PIVOT.y - .04) / 2;
  const housing = new THREE.Mesh(new THREE.CylinderGeometry(.07, .07, .14, 48), metal(0x566274, .85, .32));
  housing.rotation.x = Math.PI / 2;
  housing.position.copy(PIVOT);
  const ringMesh = new THREE.Mesh(new THREE.TorusGeometry(.066, .005, 10, 64), glow(C.act, 1.4));
  ringMesh.position.copy(PIVOT).setZ(.072);
  for (const m of [plinth, post, housing]) m.castShadow = m.receiveShadow = true;
  scene.add(plinth, post, housing, ringMesh);
}
const rod = new THREE.Group();
rod.position.copy(PIVOT);
{
  const bar = new THREE.Mesh(new THREE.CapsuleGeometry(.014, ROD, 8, 24), metal(0x9aa8bd, .8, .3));
  bar.position.y = ROD / 2;
  bar.castShadow = true;
  const strip = new THREE.Mesh(new THREE.BoxGeometry(.006, ROD * .8, .031), glow(C.act, 1.1));
  strip.position.y = ROD / 2;
  rod.add(bar, strip);
}
const bobMat = glow(C.see, .8);
const bob = new THREE.Mesh(new THREE.SphereGeometry(.045, 32, 24), bobMat);
bob.position.y = ROD;
bob.castShadow = true;
rod.add(bob);
rod.position.z = .085;
scene.add(rod);

const ghostMat = new THREE.MeshBasicMaterial({ color: C.think, transparent: true, opacity: .22, depthWrite: false, blending: THREE.AdditiveBlending });
const ghost = new THREE.Mesh(new THREE.CapsuleGeometry(.016, ROD, 8, 16), ghostMat);
ghost.geometry.translate(0, ROD / 2, 0);
ghost.position.copy(PIVOT).setZ(.085);
scene.add(ghost);

// the error arc between target and actual, and the motor's push as an arc round the axle
const arcMat = new THREE.MeshBasicMaterial({ color: C.amber, transparent: true, opacity: .55, side: THREE.DoubleSide, depthWrite: false });
const errArc = new THREE.Mesh(new THREE.BufferGeometry(), arcMat);
errArc.position.copy(PIVOT).setZ(.086);
const pushMat = new THREE.MeshBasicMaterial({ color: C.act, transparent: true, opacity: .8, side: THREE.DoubleSide, depthWrite: false });
const push = new THREE.Mesh(new THREE.BufferGeometry(), pushMat);
push.position.copy(PIVOT).setZ(.074);
scene.add(errArc, push);
const sector = (r0: number, r1: number, a0: number, a1: number): THREE.BufferGeometry => {
  const shape = new THREE.Shape(), n = 32;
  for (let i = 0; i <= n; i++) { const a = a0 + (a1 - a0) * i / n; const p: [number, number] = [-Math.sin(a) * r1, Math.cos(a) * r1]; i ? shape.lineTo(...p) : shape.moveTo(...p); }
  for (let i = n; i >= 0; i--) { const a = a0 + (a1 - a0) * i / n; shape.lineTo(-Math.sin(a) * r0, Math.cos(a) * r0); }
  return new THREE.ShapeGeometry(shape);
};
// an upright reference line and a floor stop on each side
{
  const up = new THREE.Line(new THREE.BufferGeometry().setFromPoints([PIVOT.clone().setZ(.086), PIVOT.clone().add(new THREE.Vector3(0, ROD + .12, .086))]),
    new THREE.LineDashedMaterial({ color: C.muted, dashSize: .02, gapSize: .015, transparent: true, opacity: .5 }));
  up.computeLineDistances();
  scene.add(up);
}

// --- live controls
const controls = new OrbitControls(camera, canvas);
controls.target.set(-.19, .5, 0);   // the rig sits between the left and right panels
Object.assign(controls, { enableDamping: true, minDistance: .8, maxDistance: 3, maxPolarAngle: 1.5, enabled: !recorded });
camera.position.set(-.05, .7, 2.6);
const F_MIN = 5, F_MAX = 500;
const fToSlider = (f: number): number => Math.log(f / F_MIN) / Math.log(F_MAX / F_MIN);
const sliderToF = (v: number): number => F_MIN * (F_MAX / F_MIN) ** v;
const bind = (id: string, key: keyof Gains, fmt: (v: number) => string, to = (v: number) => v, from = (v: number) => v): void => {
  const s = el<HTMLInputElement>("s" + id), out = el("v" + id);
  s.value = String(from(user[key]));
  out.textContent = fmt(user[key]);
  s.addEventListener("input", () => { user[key] = to(Number(s.value)); out.textContent = fmt(user[key]); });
};
bind("P", "P", v => v.toFixed(0));
bind("I", "I", v => v.toFixed(0));
bind("D", "D", v => v.toFixed(1));
bind("F", "f", v => v.toFixed(0) + " Hz", sliderToF, fToSlider);
bind("T", "target", v => (v >= 0 ? "+" : "") + v.toFixed(1) + "°");
el("kick").addEventListener("click", () => { kick = 1.6 * (S.th >= rad(user.target) ? 1 : -1); });
el("reset").addEventListener("click", () => { const t = S.t; S = fresh(); S.t = t; S.last = t - 1; });

// --- chart and HUD
const chart = new Chart(el("chart"), { x: [0, 8], y: [-30, 40], yTicks: [-30, -15, 0, 15, 30], xTicks: [0, 2, 4, 6, 8],
  xFormat: v => `−${(8 - v).toFixed(0)} s`, yFormat: v => `${v}°` }, 760, 230);
const place = (e: HTMLElement, v: THREE.Vector3, dx = 0, dy = 0): void => {
  const p = toScreen(v, camera, canvas);
  e.style.left = p.x + dx + "px";
  e.style.top = p.y + dy + "px";
};
const bar = (id: string, k: number, text: string, hot: boolean, low = false): void => {
  const row = el(id);
  (row.querySelector(".bar i") as HTMLElement).style.width = (clamp(k, 0, 1) * 100).toFixed(1) + "%";
  (row.querySelector(".val") as HTMLElement).textContent = text;
  row.classList.toggle("hot", hot);
  row.classList.toggle("low", low);
};

function frameAt(t: number): void {
  if (t < S.t - 1e-9) S = fresh();
  while (S.t + DT <= t + 1e-9) step();
  const g = gains(), hot = recorded ? scripted(S.t).hot : "";

  rod.rotation.z = S.th;
  ghost.rotation.z = rad(g.target);
  const err = rad(g.target) - S.th;
  errArc.geometry.dispose();
  // once it has fallen the error is no longer a correction in progress: no arc (it would spread up-left over the panels)
  errArc.geometry = Math.abs(err) > rad(.4) && !S.fell ? sector(.12, .5, Math.min(S.th, rad(g.target)), Math.max(S.th, rad(g.target))) : new THREE.BufferGeometry();
  arcMat.opacity = .08 + .3 * span(Math.abs(deg(err)), .5, 6);
  push.geometry.dispose();
  const k = S.u / U_MAX;
  push.geometry = Math.abs(k) > .01 ? sector(.085, .1, 0, -k * Math.PI * .9) : new THREE.BufferGeometry();
  push.rotation.z = S.th;
  bobMat.color.set(S.fell ? C.amber : C.see);
  bobMat.emissive.set(S.fell ? C.amber : C.see);
  bobMat.emissiveIntensity = .7 + .6 * S.flash;

  if (recorded) {
    // a slow drift at a fixed distance (no push-in: the fallen rod must stay clear of the left panels);
    // the look point sits left of the rig, so the rig is framed right of centre, between the panels
    const az = -.08 + t * .006, dist = 2.6;
    const o = controls.target.set(-.27, .5, 0);
    camera.position.set(o.x + .14 + Math.sin(az) * dist, .7, Math.cos(az) * dist);
    camera.lookAt(o);
  } else controls.update();
  render();

  // HUD
  el("clock").textContent = "T+" + t.toFixed(2).padStart(5, "0");
  el("dot").style.opacity = (t * 2 | 0) % 2 ? ".3" : "1";
  bar("gP", g.P / 200, g.P.toFixed(0), hot === "P");
  bar("gI", g.I / 150, g.I.toFixed(0), hot === "I");
  bar("gD", g.D / 20, g.D.toFixed(1), hot === "D");
  bar("gF", fToSlider(g.f), g.f.toFixed(0) + " Hz", hot === "F", g.f < 20);
  bar("gT", (g.target + 25) / 50, (g.target >= 0 ? "+" : "") + g.target.toFixed(1) + "°", false);
  el("rAngle").textContent = (deg(S.th) >= 0 ? "+" : "−") + Math.abs(deg(S.th)).toFixed(1) + "°";
  el("rErr").textContent = Math.abs(deg(err)).toFixed(1) + "°";
  el("rErrBox").classList.toggle("warn", Math.abs(deg(err)) > 1.5);
  el("rU").textContent = S.u.toFixed(0);
  el("rPeriod").textContent = (1000 / g.f).toFixed(g.f > 100 ? 1 : 0) + " ms";

  // what the last 8 s looked like
  const oscill = S.history.length > 90 ? (() => {
    const tail = S.history.slice(-120).map(p => p[1]);
    return Math.max(...tail) - Math.min(...tail);
  })() : 0;
  const t0 = S.t - 8;
  chart.draw({
    series: [{ points: S.history.map(([x, y]) => [x - t0, y] as [number, number]), colour: S.fell ? "#fbbf24" : "#38bdf8", width: 2.4 }],
    marks: [{ y: g.target, colour: "#a78bfa", label: tr("目标 θ*") }, { y: 0, colour: "#5b6b85", dash: "2 4" }],
  });

  el("stepSee").classList.toggle("on", S.flash > .4);
  el("stepThink").classList.toggle("on", S.flash > .2);
  el("stepAct").classList.toggle("on", Math.abs(S.u) > 2);
  el("seeTxt").textContent = tr("角度 {0} Hz", g.f.toFixed(0));
  // the 想 step stays "PID": the gains themselves are in the controller panel, and the short label
  // keeps the loop panel narrow enough to clear the rig
  el("actTxt").textContent = tr("力矩 {0}", `${S.u >= 0 ? "+" : "−"}${Math.abs(S.u).toFixed(0)}`);
  const [cls, head, detail] = S.fell ? ["warn", "! FALLEN", tr("倒了：控制跟不上")]
    : oscill > 3 ? ["warn", "~ OSCILLATING", tr("来回震荡")]
    : Math.abs(deg(err)) > 1.5 ? ["busy", "STEADY OFFSET", tr("稳住了，但老差 {0}°", Math.abs(deg(err)).toFixed(1))]
    : ["ok", "✓ STABLE", tr("稳在目标上")];
  const status = el("status");
  status.className = "status " + cls;
  status.innerHTML = `${head}<small>${detail}</small>`;

  const tip = new THREE.Vector3(-Math.sin(rad(g.target)) * (ROD + .09), Math.cos(rad(g.target)) * (ROD + .09), 0).add(PIVOT).setZ(.086);
  place(el("angleTag"), tip);
  el("angleTag").textContent = `θ* ${g.target >= 0 ? "+" : ""}${g.target.toFixed(0)}°`;
  const mid = (S.th + rad(g.target)) / 2;
  const errTag = el("errTag");
  place(errTag, new THREE.Vector3(-Math.sin(mid) * .56, Math.cos(mid) * .56, 0).add(PIVOT).setZ(.086), 38, 10);
  errTag.textContent = tr("误差 {0}°", Math.abs(deg(err)).toFixed(1));
  errTag.style.color = Math.abs(deg(err)) > 1.5 ? "var(--l3)" : "var(--muted)";
  errTag.style.opacity = Math.abs(deg(err)) > .4 && !S.fell ? "1" : "0";
}

run(frameAt, { dur: Number(q.get("dur") || 25.5), still: 5 });
